import { ExistingFirmResponse } from 'api/firm/firm.types';
import { MortgageClub } from 'api/mortgage-club/mortgageClub.types';
import { Network } from 'api/network/network.types';
import { Button, FormControl } from 'components/common';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { useNavigateBack } from 'hooks';
import { useBrokerFirm } from 'queries/broker/useBrokerFirm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { isStrideUpInternalFirm } from 'utils/strideUpInternalFirm';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApplicationFormPageEnum, IApplicationInProgressQuestion } from '../types';
import { parseApplicationInProgress } from '../helpers';
import { submissionQuestions } from './questions';
import {
  getOrganisationForFunnelType,
  getSubmissionRouteUIConfig,
  isValidFunnelTypeForFirm,
} from './submissionRouteConfig';
import { SubmissionFunnelTypeEnum } from './types';

function buildSectionRoute(section: ApplicationFormPageEnum): string {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set('section', section);
  return `${window.location.pathname}?${searchParams.toString()}`;
}

function OrganisationDetailsPreview({ organisation }: { organisation: Network | MortgageClub }) {
  return (
    <div className='mt-4 rounded border border-gray-200 bg-gray-50 p-4 text-sm'>
      <p className='font-medium text-gray-900'>{organisation.name}</p>
      {organisation.fcaNumber && <p className='mt-1 text-gray-700'>{organisation.fcaNumber}</p>}
    </div>
  );
}

function ReadOnlySubmissionRouteOptions({
  visibleOptions,
  selectedFunnelType,
}: {
  visibleOptions: SubmissionFunnelTypeEnum[];
  selectedFunnelType: SubmissionFunnelTypeEnum;
}) {
  const options = submissionQuestions.submissionFunnelType.options.filter(option =>
    visibleOptions.includes(option.value),
  );

  return (
    <div
      className='mt-2 grid gap-2 md:gap-[0.8rem]'
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map(option => {
        const isActive = option.value === selectedFunnelType;

        return (
          <div
            key={option.value}
            className={`flex min-w-[5rem] items-center justify-center rounded-lg border px-4 py-[0.94rem] text-center font-black ${
              isActive ? 'border-[#004615] bg-[#004615] text-white' : 'border-[#B9B9C2] bg-white text-[#4F5863]'
            }`}
          >
            <FormattedMessage id={option.labelKey} />
          </div>
        );
      })}
    </div>
  );
}

function resolveInitialFunnelType(
  firm: ExistingFirmResponse,
  savedFunnelType: SubmissionFunnelTypeEnum | '' | undefined,
): SubmissionFunnelTypeEnum {
  const config = getSubmissionRouteUIConfig(firm);

  if (savedFunnelType && isValidFunnelTypeForFirm(firm, savedFunnelType)) {
    return savedFunnelType;
  }

  return config.defaultOption;
}

export const Submission = () => {
  const intl = useIntl();
  const { id: applicationId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { data: firm, isLoading, isError } = useBrokerFirm();
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const { mutateAsync: patchApplicationInProgress, isLoading: isSaving } = useMutation(
    applicationFormApi.patchApplicationInProgress,
  );

  const currentRoute = buildSectionRoute(ApplicationFormPageEnum.SUBMISSION);
  const savedAnswers =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);

  const form = useForm<{ submissionFunnelType: SubmissionFunnelTypeEnum }>({
    defaultValues: {
      submissionFunnelType: SubmissionFunnelTypeEnum.DIRECT,
    },
  });

  useEffect(() => {
    if (!firm) {
      return;
    }

    const savedFunnelType = savedAnswers?.submissionFunnelType as SubmissionFunnelTypeEnum | undefined;

    form.reset({
      submissionFunnelType: resolveInitialFunnelType(firm, savedFunnelType),
    });
  }, [firm, savedAnswers?.submissionFunnelType, form]);

  const continueHandler = async () => {
    if (!firm) {
      return;
    }

    const config = getSubmissionRouteUIConfig(firm);
    const funnelType =
      config.selectableOptions.length > 0 ? form.getValues('submissionFunnelType') : config.defaultOption;
    const organisation = getOrganisationForFunnelType(firm, funnelType);

    const questions: IApplicationInProgressQuestion[] = [
      {
        question: intl.formatMessage({ id: submissionQuestions.submissionFunnelType.labelKey }),
        key: 'submissionFunnelType',
        answer: funnelType,
      },
    ];

    if (organisation) {
      const questionId =
        funnelType === SubmissionFunnelTypeEnum.NETWORK
          ? 'applicationForm.SUBMISSION.networkValue'
          : 'applicationForm.SUBMISSION.mortgageClubValue';

      questions.push({
        question: intl.formatMessage({ id: questionId }, { name: organisation.name }),
        key: 'organisationName',
        answer: organisation.name,
      });
    }

    await patchApplicationInProgress({
      applicationId,
      currentRoute,
      questions,
    });

    const nextSection = isStrideUpInternalFirm(firm)
      ? ApplicationFormPageEnum.INTRODUCER
      : ApplicationFormPageEnum.INTERMEDIARY_FEES;

    searchParams.set('section', nextSection);
    setSearchParams(searchParams);
  };

  if (isLoading) {
    return null;
  }

  if (isError || !firm) {
    return <FormattedMessage id='applicationForm.SUBMISSION.firmNotFound' tagName='p' />;
  }

  const routeConfig = getSubmissionRouteUIConfig(firm);
  const isSelectable = routeConfig.selectableOptions.length > 1;
  const selectedFunnelType = isSelectable ? form.watch('submissionFunnelType') : routeConfig.defaultOption;
  const organisation = getOrganisationForFunnelType(firm, selectedFunnelType);
  const isDirectApplication = selectedFunnelType === SubmissionFunnelTypeEnum.DIRECT;
  const selectableRadioOptions = submissionQuestions.submissionFunnelType.options.filter(option =>
    routeConfig.selectableOptions.includes(option.value),
  );
  const canContinue = isDirectApplication || Boolean(organisation);

  return (
    <FormProvider {...form}>
      <div>
        <div className='mt-5'>
          <FormattedMessage id={submissionQuestions.submissionFunnelType.labelKey} tagName='h4' />
          {isSelectable ? (
            <FormControl
              name='submissionFunnelType'
              radioOptions={selectableRadioOptions}
              radioGroupProps={{ className: 'mt-2' }}
              columnsAmount={selectableRadioOptions.length}
              isWide
            />
          ) : (
            <ReadOnlySubmissionRouteOptions
              visibleOptions={routeConfig.visibleOptions}
              selectedFunnelType={routeConfig.defaultOption}
            />
          )}
        </div>

        {!isDirectApplication && organisation && <OrganisationDetailsPreview organisation={organisation} />}

        <div className='mt-8 flex justify-between'>
          <Button variant='outlined' onClick={navigateBack}>
            <FormattedMessage id='cta.back' />
          </Button>

          <Button variant='contained' onClick={continueHandler} disabled={!canContinue || isSaving}>
            <FormattedMessage id='cta.continue' />
          </Button>
        </div>
      </div>
    </FormProvider>
  );
};
